import type { OrchestratorConfig, OverlayLayout, IframeState, MessageTypesType, WidgetState, WidgetDimensions } from '../types';
import { MessageBridge } from './MessageBridge';
import { MessageTypes } from '../types';
import {
  createElement,
  removeElement,
  addEventListener,
  generateId,
  isMobileViewport,
} from '../utils/domUtils';
import {
  getChatWindowStyles,
  getLauncherStyles,
  getMobileStyles,
  MUI_ELEVATION_4_SHADOW,
} from '../utils/styleUtils';

// Extracted modules
import {
  createIframeDataURL,
  buildPublicChatUrl,
  buildChannelChatUrl,
  getSandboxAttributes,
} from './IframeHtmlGenerator';
import {
  handleOverlayConfigLoaded as cfgHandleOverlayConfigLoaded,
  handleChatInterfaceReady as cfgHandleChatInterfaceReady,
  type ConfigMutableState,
} from './IframeConfigHandler';
import {
  setupResizeObserver,
  handleResize,
  getResponsiveDimensions,
  getInitialIframeStyles,
  updateResponsiveStyles,
  type ResponsiveMutableState,
} from './IframeResponsiveManager';
import {
  setResizeHandleStyles as resizeSetStyles,
  enterFullscreenMode as resizeEnterFullscreen,
  exitFullscreenMode as resizeExitFullscreen,
  destroyResizeHandles as resizeDestroyHandles,
  updateResizeHandlePositions as resizeUpdatePositions,
  type ResizeHandleStyles,
  type ResizeMutableState,
} from './IframeResizeManager';

/** All possible iframe state CSS classes. */
const IFRAME_STATE_CLASSES = [
  'kaigentic-chat-iframe--launcher', 'kaigentic-chat-iframe--minimized',
  'kaigentic-chat-iframe--expanded', 'kaigentic-chat-iframe--fullscreen'
] as const;

/** Replace the current iframe state CSS class with the given state. */
export function setIframeStateClass(el: HTMLElement, state: string): void {
  el.classList.remove(...IFRAME_STATE_CLASSES);
  el.classList.add(`kaigentic-chat-iframe--${state}`);
}

/**
 * Event handler types for iframe
 */
export type IframeLoadHandler = (iframe: HTMLIFrameElement) => void;
export type IframeStateChangeHandler = (state: IframeState) => void;

/**
 * IframeManager handles the creation and management of the chat interface iframe
 */
export class IframeManager {
  private iframe: HTMLIFrameElement | null = null;
  private state: IframeState;
  private config: OrchestratorConfig;
  private layout: OverlayLayout;
  private messageBridge: MessageBridge | null = null;
  private loadHandlers = new Set<IframeLoadHandler>();
  private stateChangeHandlers = new Set<IframeStateChangeHandler>();
  private eventCleanups: (() => void)[] = [];
  private currentWidgetState: WidgetState = 'launcher';
  private pendingResize: number | null = null;
  private visibilityTimeout: ReturnType<typeof setTimeout> | null = null;
  // Stored handler references so we can remove exactly our own listeners
  // without accidentally clearing handlers registered by other subscribers.
  private overlayConfigLoadedHandler: ((msg: any) => void) | null = null;
  private chatInterfaceReadyHandler: ((msg: any) => void) | null = null;

  // Shared mutable state objects — passed by reference to extracted modules
  // so mutations are visible without manual write-back.
  private configState: ConfigMutableState = {
    backendOverlayConfig: null,
    isChatInterfaceReady: false,
    isOpeningFromPersistState: false,
    autoOpenTimeout: null,
  };
  private resizeState: ResizeMutableState = {
    resizeHandles: new Map(),
    isDraggingResize: false,
    activeResizeEdge: null,
    resizeStartPos: { x: 0, y: 0 },
    resizeStartDimensions: { width: 0, height: 0, top: 0, left: 0 },
    resizeHandleStyles: null,
    isInFullscreenMode: false,
    pendingParentResizeMessage: null,
  };
  private responsiveState: ResponsiveMutableState = {
    resizeObserver: null,
  };

  constructor(config: OrchestratorConfig, layout: OverlayLayout) {
    this.config = config;
    this.layout = layout;
    this.state = {
      loaded: false,
      visible: false,
      communicating: false,
      lastMessageTime: 0,
      position: layout.position || 'bottom-right',
      dimensions: {
        width: layout.width || 400,
        height: layout.height || 600
      }
    };
  }

  // ─── Context builders for extracted modules ──────────────────────────────

  private get responsiveCtx() {
    return {
      iframe: this.iframe,
      layout: this.layout,
      messageBridge: this.messageBridge,
      state: this.state,
      debug: this.config.debug || false,
      getBackendOverlayConfig: () => this.configState.backendOverlayConfig,
      getActualBackendDimensions: () => this.configState.actualBackendDimensions,
      getCurrentWidgetState: () => this.currentWidgetState,
      responsiveState: this.responsiveState,
      eventCleanups: this.eventCleanups,
      resizeIframe: (d: WidgetDimensions, s: WidgetState, a: boolean) => this.resizeIframe(d, s, a),
    };
  }

  private get configCtx() {
    return {
      iframe: this.iframe,
      layout: this.layout,
      messageBridge: this.messageBridge,
      configState: this.configState,
      debug: this.config.debug || false,
      makeVisible: () => this.makeVisible(),
      getResponsiveDimensions: (state: WidgetState) => this.getResponsiveDimensions(state),
      resizeIframe: (d: WidgetDimensions, s: WidgetState, a: boolean) => this.resizeIframe(d, s, a),
      notifyStateChange: () => this.notifyStateChange(),
      updateState: (partial: { position?: string; dimensions?: { width: number; height: number } }) => {
        if (partial.position) this.state.position = partial.position as OverlayLayout['position'];
        if (partial.dimensions) this.state.dimensions = partial.dimensions;
      },
      updateLayout: (partial: Partial<OverlayLayout>) => {
        this.layout = { ...this.layout, ...partial };
      },
    };
  }

  private get resizeCtx() {
    return {
      iframe: this.iframe,
      layout: this.layout,
      messageBridge: this.messageBridge,
      state: this.state,
      eventCleanups: this.eventCleanups,
      resizeState: this.resizeState,
    };
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  async create(): Promise<HTMLIFrameElement> {
    if (this.iframe) {
      this.destroy();
    }

    this.iframe = createElement('iframe', {
      id: generateId('chat-iframe'),
      className: 'kaigentic-chat-iframe',
      attributes: {
        'sandbox': getSandboxAttributes(this.config),
        'referrerpolicy': this.config.iframe?.referrerPolicy || 'strict-origin-when-cross-origin',
        'allow': 'microphone'
      },
      styles: {
        ...getInitialIframeStyles(this.responsiveCtx),
        pointerEvents: 'auto'
      } as Partial<CSSStyleDeclaration>
    });

    if (this.config.iframe?.allowFullscreen) {
      this.iframe.setAttribute('allowfullscreen', 'true');
    }

    // Register message handlers BEFORE setting iframe.src so they are in place
    // for any messages the iframe sends during its early initialization phase.
    // Messages that arrive before these handlers are registered get marked as
    // processed by MessageBridge and are never replayed — causing a permanent
    // blank widget (KAIG-1614).
    if (this.messageBridge) {
      this.setupNewMessageHandlers();
    }

    // Set iframe source - choose based on mode
    if (this.config.channelChatUrl) {
      this.iframe.src = buildChannelChatUrl(this.config);
      if (this.config.debug) {
        console.log('[IframeManager] Using channel chat mode with URL:', this.iframe.src);
      }
    } else if (this.config.publicChatMode) {
      this.iframe.src = buildPublicChatUrl(this.config);
      if (this.config.debug) {
        console.log('[IframeManager] Using public chat mode with URL:', this.iframe.src);
      }
    } else {
      this.iframe.src = createIframeDataURL(this.config, this.layout, this.configState.backendOverlayConfig);
      if (this.config.debug) {
        console.log('[IframeManager] Using webcomponent mode with blob URL');
      }
    }

    const loadCleanup = addEventListener(this.iframe, 'load', () => {
      this.handleIframeLoad();
    });
    this.eventCleanups.push(loadCleanup);

    const errorCleanup = addEventListener(this.iframe, 'error', (e) => {
      console.error('[IframeManager] Iframe load error:', e);
      this.state.loaded = false;
      this.notifyStateChange();
    });
    this.eventCleanups.push(errorCleanup);

    if (this.layout.responsive) {
      setupResizeObserver(this.responsiveCtx);
      updateResponsiveStyles(this.responsiveCtx);
    }

    document.body.appendChild(this.iframe);
    return this.iframe;
  }

  show(): void {
    if (!this.iframe) return;
    this.state.visible = true;
    Object.assign(this.iframe.style, getChatWindowStyles(this.layout, true));
    if (this.layout.mobileFullScreen && isMobileViewport()) {
      Object.assign(this.iframe.style, getMobileStyles(this.layout));
    }
    this.notifyStateChange();
  }

  hide(): void {
    if (!this.iframe) return;
    this.state.visible = false;
    Object.assign(this.iframe.style, getLauncherStyles(this.layout));
    this.notifyStateChange();
  }

  toggle(): void {
    if (this.state.visible) { this.hide(); } else { this.show(); }
  }

  makeVisible(): void {
    if (!this.iframe) return;
    if (this.visibilityTimeout !== null) {
      clearTimeout(this.visibilityTimeout);
      this.visibilityTimeout = null;
    }
    this.iframe.style.opacity = '1';
    if (this.config.debug) {
      console.log('[IframeManager] Iframe made visible (fade-in)');
    }
  }

  updateLayout(newLayout: Partial<OverlayLayout>): void {
    this.layout = { ...this.layout, ...newLayout };
    if (this.iframe) {
      Object.assign(this.iframe.style, getChatWindowStyles(this.layout, this.state.visible));
      if (this.layout.responsive) {
        handleResize(this.responsiveCtx);
        updateResponsiveStyles(this.responsiveCtx);
      }
    }
    this.state.position = this.layout.position || 'bottom-right';
    this.state.dimensions = { width: this.layout.width || 400, height: this.layout.height || 600 };
    this.notifyStateChange();
    if (this.messageBridge && this.state.communicating) {
      this.messageBridge.send(MessageTypes.UPDATE_LAYOUT, this.layout);
    }
  }

  updateConfig(newConfig: Partial<OrchestratorConfig>): void {
    const previousConfig = { ...this.config };
    try {
      const mergedConfig = this.mergeConfigWithConflictResolution(this.config, newConfig);
      this.config = mergedConfig;
      this.sendConfigUpdateWithRetry(MessageTypes.UPDATE_CONFIG, this.config, previousConfig);
    } catch (error) {
      console.error('[IframeManager] Config update failed, rolling back:', error);
      this.config = previousConfig;
      throw error;
    }
  }

  private mergeConfigWithConflictResolution<T extends Record<string, any>>(
    currentConfig: T,
    newConfig: Partial<T>
  ): T {
    const result = { ...currentConfig };
    for (const key in newConfig) {
      const newValue = newConfig[key];
      const currentValue = currentConfig[key];
      if (newValue !== undefined) {
        if (key === 'layout' && typeof newValue === 'object' && typeof currentValue === 'object') {
          const currentLayout = currentValue as any;
          result[key] = {
            ...currentValue, ...newValue,
            ...(currentLayout.iframeOverrides ? { iframeOverrides: currentLayout.iframeOverrides } : {})
          } as T[Extract<keyof T, string>];
        } else if (Array.isArray(newValue) && Array.isArray(currentValue)) {
          result[key] = [...new Set([...currentValue, ...newValue])] as T[Extract<keyof T, string>];
        } else {
          result[key] = newValue as T[Extract<keyof T, string>];
        }
      }
    }
    return result;
  }

  private async sendConfigUpdateWithRetry(
    messageType: MessageTypesType, config: any, fallbackConfig: any, maxRetries: number = 3
  ): Promise<void> {
    if (!this.messageBridge) {
      console.warn('[IframeManager] No message bridge available for config update');
      return;
    }
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (this.state.communicating) {
          this.messageBridge.send(messageType, config);
          return;
        } else if (this.messageBridge && !this.state.communicating) {
          this.state.communicating = true;
          this.messageBridge.send(messageType, config);
          return;
        }
      } catch (error) {
        console.warn(`[IframeManager] Config update attempt ${attempt} failed:`, error);
        if (attempt === maxRetries) {
          console.error('[IframeManager] All config update attempts failed, using fallback');
          this.config = fallbackConfig;
          throw new Error(`Config update failed after ${maxRetries} attempts: ${error}`);
        }
        await new Promise(resolve => setTimeout(resolve, 100 * attempt));
      }
    }
  }

  // ─── Message Bridge ─────────────────────────────────────────────────────

  setMessageBridge(bridge: MessageBridge): void {
    this.messageBridge = bridge;
    if (this.iframe && this.iframe.contentWindow) {
      bridge.setTargetWindow(this.iframe.contentWindow);
    }
  }

  sendMessage(type: MessageTypesType, data?: any): void {
    if (this.messageBridge) { this.messageBridge.send(type, data); }
  }

  async requestFromIframe<T = any>(type: MessageTypesType, data?: any): Promise<T> {
    if (!this.messageBridge) { throw new Error('No message bridge available'); }
    return this.messageBridge.request<T>(type, data);
  }

  // ─── Event Handlers ─────────────────────────────────────────────────────

  onLoad(handler: IframeLoadHandler): () => void {
    this.loadHandlers.add(handler);
    return () => this.loadHandlers.delete(handler);
  }

  onStateChange(handler: IframeStateChangeHandler): () => void {
    this.stateChangeHandlers.add(handler);
    return () => this.stateChangeHandlers.delete(handler);
  }

  // ─── Getters ────────────────────────────────────────────────────────────

  getState(): IframeState { return { ...this.state }; }
  getElement(): HTMLIFrameElement | null { return this.iframe; }
  isVisible(): boolean { return this.state.visible; }
  isLoaded(): boolean { return this.state.loaded; }
  getCurrentWidgetState(): WidgetState { return this.currentWidgetState; }
  isFullscreen(): boolean { return this.resizeState.isInFullscreenMode; }

  // ─── Responsive dimensions (delegated) ──────────────────────────────────

  getResponsiveDimensions(state: WidgetState): WidgetDimensions {
    return getResponsiveDimensions(this.responsiveCtx, state);
  }

  // ─── Iframe resizing ───────────────────────────────────────────────────

  resizeIframe(dimensions: WidgetDimensions, state: WidgetState, animated: boolean = true): void {
    if (!this.iframe) return;

    if (this.pendingResize) {
      cancelAnimationFrame(this.pendingResize);
      this.pendingResize = null;
    }

    // Update state and CSS class synchronously so other code paths
    // (e.g., ResizeObserver handleResize) see the correct state immediately
    this.currentWidgetState = state;
    this.state.visible = state !== 'launcher';
    setIframeStateClass(this.iframe, state);

    // Defer dimension/style changes to next animation frame for smooth rendering
    const resize = () => {
      if (!this.iframe) return;

      this.iframe.style.transition = (animated && this.layout.animations)
        ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none';

      this.iframe.style.width = typeof dimensions.width === 'number'
        ? `${dimensions.width}px` : dimensions.width;
      this.iframe.style.height = typeof dimensions.height === 'number'
        ? `${dimensions.height}px` : dimensions.height;

      if (dimensions.position) {
        this.iframe.style.top = '';
        this.iframe.style.left = '';
        this.iframe.style.right = '';
        this.iframe.style.bottom = '';

        const pos = dimensions.position;
        if (pos.top !== undefined) this.iframe.style.top = typeof pos.top === 'number' ? `${pos.top}px` : pos.top;
        if (pos.left !== undefined) this.iframe.style.left = typeof pos.left === 'number' ? `${pos.left}px` : pos.left;
        if (pos.right !== undefined) this.iframe.style.right = typeof pos.right === 'number' ? `${pos.right}px` : pos.right;
        if (pos.bottom !== undefined) this.iframe.style.bottom = typeof pos.bottom === 'number' ? `${pos.bottom}px` : pos.bottom;
      }

      if (dimensions.borderRadius !== undefined) {
        this.iframe.style.borderRadius = `${dimensions.borderRadius}px`;
      }
      if (dimensions.zIndex !== undefined) {
        this.iframe.style.zIndex = dimensions.zIndex.toString();
      }

      // Iframe-level shadow: applied only in chat-window state (the launcher
      // has its own treatment) and only when the customer enabled
      // layout.shadow. The inner Paper's shadow is suppressed by
      // iframeOverrides because it would be clipped at the iframe boundary,
      // so the visible separation has to come from the iframe element itself.
      this.iframe.style.boxShadow =
        state !== 'launcher' && this.layout.shadow ? MUI_ELEVATION_4_SHADOW : 'none';

      this.iframe.style.pointerEvents = 'auto';

      if (this.resizeState.resizeHandles.size > 0) {
        resizeUpdatePositions(this.resizeCtx);
      }

      this.notifyStateChange();
    };

    this.pendingResize = requestAnimationFrame(resize);
  }

  handleWidgetStateChange(state: WidgetState, customDimensions?: WidgetDimensions, animated: boolean = true): void {
    this.currentWidgetState = state;

    if (this.iframe) {
      setIframeStateClass(this.iframe, state);
    }

    const dimensions = this.getResponsiveDimensions(state);

    if (customDimensions && state === 'expanded') {
      this.configState.actualBackendDimensions = {
        width: typeof customDimensions.width === 'number'
          ? customDimensions.width : parseInt(String(customDimensions.width)) || 400,
        height: typeof customDimensions.height === 'number'
          ? customDimensions.height : parseInt(String(customDimensions.height)) || 600
      };
    }

    this.resizeIframe(dimensions, state, animated);

    this.state.dimensions = {
      width: typeof dimensions.width === 'number' ? dimensions.width : 0,
      height: typeof dimensions.height === 'number' ? dimensions.height : 0
    };

    updateResponsiveStyles(this.responsiveCtx, state);
  }

  // ─── Fullscreen resize handles (delegated) ─────────────────────────────

  setResizeHandleStyles(styles: ResizeHandleStyles): void {
    this.resizeState.resizeHandleStyles = styles;
    resizeSetStyles(this.resizeCtx, styles);
  }

  enterFullscreenMode(): void {
    resizeEnterFullscreen(this.resizeCtx);
  }

  exitFullscreenMode(): void {
    resizeExitFullscreen(this.resizeCtx);
  }

  // ─── Destroy ────────────────────────────────────────────────────────────

  destroy(): void {
    if (this.visibilityTimeout !== null) {
      clearTimeout(this.visibilityTimeout);
      this.visibilityTimeout = null;
    }
    if (this.configState.autoOpenTimeout) {
      clearTimeout(this.configState.autoOpenTimeout);
      this.configState.autoOpenTimeout = null;
    }
    this.eventCleanups.forEach(cleanup => cleanup());
    this.eventCleanups.length = 0;
    if (this.responsiveState.resizeObserver) {
      this.responsiveState.resizeObserver.disconnect();
      this.responsiveState.resizeObserver = null;
    }
    if (this.pendingResize) { cancelAnimationFrame(this.pendingResize); this.pendingResize = null; }
    if (this.resizeState.pendingParentResizeMessage !== null) {
      cancelAnimationFrame(this.resizeState.pendingParentResizeMessage);
      this.resizeState.pendingParentResizeMessage = null;
    }
    resizeDestroyHandles(this.resizeCtx);
    if (this.iframe) { removeElement(this.iframe); this.iframe = null; }
    this.state.loaded = false;
    this.state.visible = false;
    this.state.communicating = false;
    this.loadHandlers.clear();
    this.stateChangeHandlers.clear();
    this.messageBridge = null;
  }

  // ─── Private helpers ───────────────────────────────────────────────────

  private handleIframeLoad(): void {
    this.state.loaded = true;
    if (this.messageBridge && this.iframe?.contentWindow) {
      // setTargetWindow enables SENDING messages back to the iframe.
      // setupNewMessageHandlers() was already called in create() before iframe.src
      // was set, so handlers are already in place — do not call it again here.
      this.messageBridge.setTargetWindow(this.iframe.contentWindow);
    }

    // Safety net: if the widget hasn't become visible within 10 s, force it
    // visible — but ONLY if the iframe sent at least one message (proving it
    // actually loaded). The real fallback scenario is: React mounted in the
    // iframe (CHAT_INTERFACE_READY arrived) but OVERLAY_CONFIG_LOADED never
    // arrived because the backend is slow or unreachable. Iframes that sent NO
    // messages were likely blocked by CSP or a network error; making those
    // visible would show a broken blank widget, which is worse than invisible.
    const VISIBILITY_TIMEOUT_MS = 10_000;
    this.visibilityTimeout = setTimeout(() => {
      this.visibilityTimeout = null;
      if (this.iframe && this.iframe.style.opacity !== '1') {
        const iframeLoaded =
          this.configCtx.configState.isChatInterfaceReady ||
          this.configCtx.configState.backendOverlayConfig !== null;
        if (iframeLoaded) {
          console.warn('[IframeManager] Visibility timeout — forcing makeVisible (OVERLAY_CONFIG_LOADED may have been lost)');
          this.makeVisible();
        } else if (this.config.debug) {
          console.warn('[IframeManager] Visibility timeout — iframe sent no messages; likely blocked by CSP or a network error. Not forcing visible.');
        }
      }
    }, VISIBILITY_TIMEOUT_MS);

    this.notifyStateChange();
    this.loadHandlers.forEach(handler => {
      try { handler(this.iframe!); }
      catch (error) { console.error('[IframeManager] Load handler error:', error); }
    });
  }

  private setupNewMessageHandlers(): void {
    if (!this.messageBridge) return;

    // Remove only IframeManager's own handlers using stored references,
    // so we don't accidentally clear handlers registered by other subscribers
    // (e.g. the orchestrator's CHAT_INTERFACE_READY handler that emits the
    // public 'chat-interface-ready' event).
    if (this.overlayConfigLoadedHandler) {
      this.messageBridge.off(MessageTypes.OVERLAY_CONFIG_LOADED, this.overlayConfigLoadedHandler);
    }
    if (this.chatInterfaceReadyHandler) {
      this.messageBridge.off(MessageTypes.CHAT_INTERFACE_READY, this.chatInterfaceReadyHandler);
    }

    this.overlayConfigLoadedHandler = (message) => {
      cfgHandleOverlayConfigLoaded(this.configCtx, message);
    };
    this.chatInterfaceReadyHandler = (message) => {
      cfgHandleChatInterfaceReady(this.configCtx, message);
    };

    this.messageBridge.on(MessageTypes.OVERLAY_CONFIG_LOADED, this.overlayConfigLoadedHandler);
    this.messageBridge.on(MessageTypes.CHAT_INTERFACE_READY, this.chatInterfaceReadyHandler);
  }

  private notifyStateChange(): void {
    this.stateChangeHandlers.forEach(handler => {
      try { handler(this.getState()); }
      catch (error) { console.error('[IframeManager] State change handler error:', error); }
    });
  }
}
